/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2026].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2026 Scale Microgrid Solutions Operating, LLC
 * <legal@scalemicrogrids.com>
 */

// Mirrors scaleos/alarm_severity_mapping.py — keep the two in sync.
// Ordered most to least severe; every severity comparison derives from it.
export const ALARM_SEVERITIES = ["Critical", "High", "Medium", "Low"] as const;

export type AlarmSeverity = (typeof ALARM_SEVERITIES)[number];

export const DEFAULT_ALARM_SEVERITY: AlarmSeverity = "Medium";

// Unmigrated edge rows may still carry Red/Amber; treat as High/Medium.
// The old high/medium count buckets already match case-insensitively.
const LEGACY_ALARM_SEVERITY: Record<string, AlarmSeverity> = {
  red: "High",
  amber: "Medium",
};

// Scale Canopy Prism — Alarms (app-wide)
// Red, Orange, Yellow, Grey
const ALARM_SEVERITY_COLORS: Record<AlarmSeverity, string> = {
  Critical: "#e53935",
  High: "#ffa726",
  Medium: "#ffeb3b",
  Low: "#616161",
};

// Severities come from the Postgres alert_type column, a CHAR(10) that pads
// with spaces and whose casing varies by data source, so match leniently.
export const normalizeAlarmSeverity = (severity?: string | null): AlarmSeverity => {
  const trimmed = severity?.trim().toLowerCase();
  if (!trimmed) {
    return DEFAULT_ALARM_SEVERITY;
  }
  return (
    ALARM_SEVERITIES.find((known) => known.toLowerCase() === trimmed) ??
    LEGACY_ALARM_SEVERITY[trimmed] ??
    DEFAULT_ALARM_SEVERITY
  );
};

// Lower rank is more severe, matching ALARM_SEVERITIES order.
export const getAlarmSeverityRank = (severity?: string | null): number =>
  ALARM_SEVERITIES.indexOf(normalizeAlarmSeverity(severity));

export const getAlarmSeverityColor = (severity?: string | null): string =>
  ALARM_SEVERITY_COLORS[normalizeAlarmSeverity(severity)];
